// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
    // Try to get from localStorage first (fallback)
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    console.log('Retrieved token:', token ? 'Token exists' : 'No token found');
    return token;
};

export async function fetchAvailableUnits(projectId, page = 1, limit = 16) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making fetch available units request to:', `${API_BASE_URL}api/v1/dashboard/unit/available-units`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/available-units?page=${page}&limit=${limit}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            project_id: projectId,
        }),
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch available units';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function fetchUnavailableUnits(projectId, page = 1, limit = 16) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making fetch unavailable units request to:', `${API_BASE_URL}api/v1/dashboard/unit/unavailable-units`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/unavailable-units?page=${page}&limit=${limit}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            project_id: projectId,
        }),
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch unavailable units';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function createUnit(formData) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {};

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    // Don't set Content-Type for FormData, let the browser set it with boundary
    console.log('Making create unit request to:', `${API_BASE_URL}api/v1/dashboard/unit/create`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/create`, {
        method: 'POST',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to create unit';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function fetchUnitDetails(unitId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making fetch unit details request to:', `${API_BASE_URL}api/v1/dashboard/unit/${unitId}`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/${unitId}`, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch unit details';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function updateUnit(unitId, updateData) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {};

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    // Check if updateData is FormData (for image updates) or regular object
    const isFormData = updateData instanceof FormData;

    // Don't set Content-Type for FormData, let the browser set it with boundary
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    console.log('Making update unit request to:', `${API_BASE_URL}api/v1/dashboard/unit/${unitId}`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/${unitId}`, {
        method: 'PATCH',
        headers,
        body: isFormData ? updateData : JSON.stringify(updateData),
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to update unit';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function addUnitCustomer(data) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making add unit customer request to:', `${API_BASE_URL}api/v1/dashboard/unit/add-unit-customer`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/add-unit-customer`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to add customer to unit';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function removeUnitCustomer(unitId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Validate unitId
    if (!unitId || typeof unitId !== 'string') {
        throw new Error('Valid unit ID is required');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making remove unit customer request to:', `${API_BASE_URL}api/v1/dashboard/unit/remove-unit-customer`);
    console.log('Unit ID being sent:', unitId);

    // Option 1: Unit ID in URL path (following pattern of other DELETE operations)
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/remove-unit-customer`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ unit_id: unitId }),
    });

    // Option 2: Unit ID in request body (as originally specified)
    // Uncomment this and comment out Option 1 if backend expects body format:
    /*
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/remove-unit-customer`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ unit_id: unitId }),
    });
    */

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to remove customer from unit';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            console.error('API Error Response:', errorData);
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function deleteUnit(unitId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making delete unit request to:', `${API_BASE_URL}api/v1/dashboard/unit/${unitId}`);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/${unitId}`, {
        method: 'DELETE',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to delete unit';
        const responseClone = response.clone();

        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            try {
                const errorText = await responseClone.text();
                console.error('Non-JSON response:', errorText);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            } catch (textError) {
                console.error('Failed to read response as text:', textError);
                errorMessage = `Server error: ${response.status} ${response.statusText}`;
            }
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

export async function fetchUnitUpdates(unitId, page = 1, limit = 8) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const url = `${API_BASE_URL}api/v1/dashboard/unit-update?unit_id=${unitId}&page=${page}&limit=${limit}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch unit updates';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function fetchUnitUpdateDetails(updateId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const url = `${API_BASE_URL}api/v1/dashboard/unit-update/${updateId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch unit update details';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function createUnitUpdate({ unit_id, description, images }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const formData = new FormData();
    formData.append('unit_id', unit_id);
    formData.append('description', description);
    if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
            formData.append('images', images[i]);
        }
    }
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit-update/create`, {
        method: 'POST',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to create unit update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function deleteUnitUpdate(updateId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit-update/delete/${updateId}`, {
        method: 'DELETE',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to delete unit update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function updateUnitUpdate(updateId, { description, images, old_img }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const formData = new FormData();
    if (description !== undefined) formData.append('description', description);

    // Add new images
    if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
            formData.append('images', images[i]);
        }
    }

    // Add old images that should remain
    if (old_img && old_img.length > 0) {
        for (let i = 0; i < old_img.length; i++) {
            formData.append('old_img', old_img[i]);
        }
    }

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit-update/update/${updateId}`, {
        method: 'PATCH',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to update unit update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function fetchSwagUpdates(page = 1, limit = 16) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const url = `${API_BASE_URL}api/v1/dashboard/post?page=${page}&limit=${limit}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch swag updates';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function fetchSwagUpdateDetails(postId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const url = `${API_BASE_URL}api/v1/dashboard/post/${postId}`;
    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch swag update details';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function deleteSwagUpdate(postId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/post/delete/${postId}`, {
        method: 'DELETE',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to delete swag update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function updateSwagUpdate(postId, { title, description, logo, images, old_img }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    const formData = new FormData();
    if (title !== undefined) formData.append('title', title);
    if (description !== undefined) formData.append('description', description);
    if (logo) formData.append('logo', logo);
    // Add new images
    if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
            formData.append('images', images[i]);
        }
    }
    // Add old images that should remain
    if (old_img && old_img.length > 0) {
        for (let i = 0; i < old_img.length; i++) {
            formData.append('old_img', old_img[i]);
        }
    }
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/post/update/${postId}`, {
        method: 'PATCH',
        headers,
        body: formData,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to update swag update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function createSwagUpdate(data) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }
    const accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }
    let body;
    // Accept FormData directly (from AddSwagUpdate) or object (legacy)
    if (data instanceof FormData) {
        body = data;
    } else {
        // fallback: build FormData from object
        body = new FormData();
        if (data.title) body.append('title', data.title);
        if (data.description) body.append('description', data.description);
        if (data.logo) body.append('logo', data.logo);
        if (data.images && data.images.length > 0) {
            data.images.forEach(img => body.append('images', img));
        }
    }
    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/post/create`, {
        method: 'POST',
        headers,
        body,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to create swag update';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }
    return response.json();
}

export async function searchUnits(projectId, query) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    // Get token using helper function
    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    // Add authorization header and cookie with JWT token
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const url = new URL(`${API_BASE_URL}api/v1/dashboard/unit/search`);
    url.searchParams.append('project_id', projectId);
    url.searchParams.append('label', query);

    console.log('Making search units request to:', url.toString());

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
    });

    // Check for 401 status
    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to search units';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

// Fetch customer unit data for editing
export async function fetchCustomerUnitData(unitId) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/get-customer-unit?unit_id=${unitId}`, {
        method: 'GET',
        headers,
    });

    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to fetch customer unit data';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

// Update customer unit data
// export async function updateCustomerUnitData(data) {
//     const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
//     if (!API_BASE_URL) {
//         throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
//     }

//     const accessToken = getAuthToken();
//     const headers = {
//         'Content-Type': 'application/json',
//     };

//     if (accessToken) {
//         headers['Authorization'] = `Bearer ${accessToken}`;
//         headers['Cookie'] = `jwt=${accessToken}`;
//     }

//     const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/update-customer-unit`, {
//         method: 'PATCH',
//         headers,
//         body: JSON.stringify(data),
//     });

//     checkResponseStatus(response);

//     if (!response.ok) {
//         let errorMessage = 'Failed to update customer unit data';
//         try {
//             const errorData = await response.json();
//             errorMessage = errorData.message || errorMessage;
//         } catch (e) {
//             errorMessage = `Server error: ${response.status} ${response.statusText}`;
//         }
//         throw new Error(errorMessage);
//     }

//     return response.json();
// }

export async function updateCustomerUnitDataEdit(requestData) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    if (!API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
    }

    const accessToken = getAuthToken();

    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        headers['Cookie'] = `jwt=${accessToken}`;
    }

    console.log('Making update customer unit edit request to:', `${API_BASE_URL}api/v1/dashboard/unit/update-customer-unit`);
    console.log('Request data:', requestData);

    const response = await fetch(`${API_BASE_URL}api/v1/dashboard/unit/update-customer-unit`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(requestData),
    });

    checkResponseStatus(response);

    if (!response.ok) {
        let errorMessage = 'Failed to update customer unit data';
        try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
        } catch (e) {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
    }

    return response.json();
}
